import React, { useState, useEffect, useCallback } from "react";
import * as customerReportService from "../../../services/customerReport.service";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomPagination from "../../../common/custompagination";
import Breadcrums from "../../../common/breadcrumbs";
import { useTranslation } from "react-i18next";
import CustomRangepicker from "../../../common/rangepicker";
import { Placeholder } from "rsuite";
import Sorting from "../../../common/sorting";
import { formateDateWithMonthAdmin } from "../../../../../utils/commonfunction";
import ReactApexChart from 'react-apexcharts'
import CustomerChart from "./customerChart";

const CustomerReportList = () => {

    const [list, setList] = useState([]);
    const [loader, setLoader] = useState(true);
    const [globalsearch, setGlobalSearch] = useState("");
    const [sorting, setSorting] = useState({});
    const [page, setPage] = useState(1);
    const [itemperpage] = useState(10);
    const [datalength, setDataLength] = useState(0);
    const [statsupdate, setStatusUpdate] = useState("false");
    const [resetdate, setResetDate] = useState(false);
    const [search, setSearch] = useState({});
    const [defaultSorting, setDefaultSorting] = useState(true);
    const [filter, setFilter] = useState("30");
    const params = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const breadcrumbs = [
        { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
        { title: t("Customer Report"), url: "" },
    ];

    // ─── Table Data ─────────────────────────────────────────────────────────────
    useEffect(() => {
        const getData = setTimeout(() => {
            if (search) {
                setLoader(true);
                const formData = new FormData();
                formData.append("page", params.pgno);
                formData.append("per_page", itemperpage);
                formData.append("global_search", globalsearch);
                formData.append("sort", JSON.stringify(sorting));
                formData.append("search", JSON.stringify(search));

                customerReportService.List(formData)
                    .then((data) => {
                        setDataLength(data?.data?.total_records || 0);
                        setList(data?.data?.list || []);
                        setPage(data?.data?.page ? data?.data?.page : 1);
                        setLoader(false);
                    })
                    .catch((error) => {
                        console.error("Error fetching customer reports:", error);
                        setLoader(false);
                    });
            }
        }, 300);

        return () => clearTimeout(getData);
    }, [location, statsupdate, sorting, search, globalsearch, itemperpage, params.pgno]);

    // ─── Search / Filter Helpers ─────────────────────────────────────────────────
    const prepareSearch = (key, value) => {
        let sr = { ...search };
        if (String(value).length > 0) {
            sr[key] = value;
        } else {
            delete sr[key];
        }
        setSearch(sr);
    };

    const resetFilter = (e) => {
        e.preventDefault();
        setGlobalSearch("");
        setSearch({});
        setResetDate(!resetdate);
        setDefaultSorting(true);
        setSorting({});
    };

    const handleSort = (e, column) => {
        setDefaultSorting(false);
        let sort = { order: 0, column: column };
        if (e.target.classList.contains("assc")) {
            sort.order = -1;
        } else {
            sort.order = 1;
        }
        setSorting(sort);
    };

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div style={{
                    display: "flex",
                }}>
                    <div className="mb-4 flex items-center space-x-2">
                        <label className="mr-2 font-medium text-gray-700">Select Duration:</label>
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className="px-4 py-2 rounded-xl border border-gray-300 bg-white shadow-md text-gray-700 
                focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500
                transition duration-200 ease-in-out hover:border-indigo-400"
                        >
                            <option value="7">Last 7 Days</option>
                            <option value="30">Last 30 Days</option>
                            <option value="90">Last 90 Days</option>
                            <option value="all">All</option>
                        </select>
                    </div>

                </div>
                <CustomerChart filter={filter} From="CustomerReport" />

                {/* Table Card */}
                <div className="card custom-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="main-content-label">
                                {t("Customer Report")}
                            </h6>
                            <div className="d-flex align-items-center">
                                <div className="form-group mb-0 me-3">
                                    <div className="form-group mb-0 rangepicker_container filter_design">
                                        <i className="fa fa-search calender_icon"></i>
                                        <input
                                            type="search"
                                            className="form-control"
                                            value={globalsearch}
                                            placeholder={t("input_placeholder_search")}
                                            onChange={(e) => setGlobalSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="me-3">
                                    <CustomRangepicker
                                        GetDateRange={(e) => prepareSearch("createdAt", e)}
                                        resetdate={resetdate}
                                    />
                                </div>
                                <button
                                    type="reset"
                                    value="Reset"
                                    onClick={resetFilter}
                                    className="btn btn-warning float-right mr-2"
                                >
                                    {t("btn_reset_filter")}
                                </button>
                            </div>
                        </div>

                        {loader ? (
                            <Placeholder.Paragraph
                                className="w-100"
                                rowHeight={30}
                                rowSpacing={5}
                                active={true}
                                rows={7}
                            />
                        ) : (
                            <>
                                <div className="table-responsive min-h-400">
                                    <table className="table table-bordered border-t0 card-table border-top text-nowrap w-100">
                                        <thead>
                                            <tr>
                                                <th className="position-relative select_head">
                                                    <span>{t("list_heading_sno")}</span>
                                                </th>
                                                <th>
                                                    <div className="sorting_column">
                                                        <span>{t("label_name")}</span>
                                                        <Sorting
                                                            sort={sorting}
                                                            handleSort={handleSort}
                                                            defaultSorting={defaultSorting}
                                                            column="name"
                                                        />
                                                    </div>
                                                </th>
                                                <th>
                                                    <span>{t("label_phone")}</span>
                                                </th>
                                                <th className="status_head">
                                                    <div className="sorting_column">
                                                        <span>{t("label_email")}</span>
                                                        <Sorting
                                                            sort={sorting}
                                                            handleSort={handleSort}
                                                            defaultSorting={defaultSorting}
                                                            column="email"
                                                        />
                                                    </div>
                                                </th>
                                                <th><span>{t("Status")}</span></th>
                                                <th><span>{t("Gender")}</span></th>
                                                <th><span>{t("Login Provider")}</span></th>

                                                <th className="created_head">
                                                    <div className="">
                                                        <span>{t("Viewed Streams")}</span>
                                                    </div>
                                                </th>
                                                <th><span>{t("Liked Streams")}</span></th>
                                                <th><span>{t("Created Streams")}</span></th>
                                                <th><span>{t("Created Leads")}</span></th>
                                                <th><span>{t("Last Active")}</span></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {list.length ? (
                                                list.map((row, i) => (
                                                    <tr key={row._id || i}>
                                                        <td>{(page - 1) * itemperpage + i + 1}</td>
                                                        <td>{row?.name || "N/A"}</td>
                                                        <td>{row?.phone_number || "-"}</td>
                                                        <td>{row?.email || "N/A"}</td>
                                                        <td>{row?.status === 1 ? "Active" : "Inactive"}</td>
                                                        <td>{row?.gender ? row.gender.charAt(0).toUpperCase() + row.gender.slice(1) : "N/A"}</td>
                                                        <td>{row?.accountProvider ? row.accountProvider : "Normal"}</td>
                                                        <td>{row?.viewed_streams_count || 0}</td>
                                                        <td>{row?.liked_streams_count || 0}</td>
                                                        <td>{row?.created_streams_count || 0}</td>
                                                        <td>{row?.created_leads_count || 0}</td>
                                                        <td>
                                                            {row?.updatedAt
                                                                ? formateDateWithMonthAdmin(row.updatedAt)
                                                                : "N/A"}
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan={12} className="text-center">
                                                        {t("msg_no_records")}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="" id="example1_info" role="status" aria-live="polite">
                                    <b>{t("msg_total_records")} : {datalength || "0"}</b>
                                </div>

                                {datalength > 0 && (
                                    <CustomPagination
                                        datalength={datalength}
                                        itemperpage={itemperpage}
                                        currentPage={page}
                                        setPage={setPage}
                                        pageRoute={[
                                            {
                                                name: "",
                                                path: "/admin/reports/customers/list",
                                            },
                                        ]}
                                    />
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomerReportList;